const defaultPublicDomainSuffix = 'pitaliy.com';
const defaultPitaliyLoaderUrl = 'https://pitaliy.com/pitaliy.js';

export const PITALIY_LOADER_URL =
  import.meta.env.VITE_PITALIY_LOADER_URL ?? defaultPitaliyLoaderUrl;
export const PUBLIC_DOMAIN_SUFFIX =
  import.meta.env.VITE_PUBLIC_DOMAIN_SUFFIX ?? defaultPublicDomainSuffix;

/**
 * Normalizes domain suffix (e.g. ".pitaliy.com" or "pitaliy.com") to "pitaliy.com".
 */
function normalizeDomainSuffix(suffix: string): string {
  return suffix ? suffix.replace(/^\.+/, '') : '';
}

/** Host labels that should be treated as root domain aliases, not public subdomains. */
const RESERVED_ROOT_SUBDOMAINS = new Set(['www']);

/**
 * Normalizes the extracted subdomain part.
 * - "www" => "" (treat as root domain alias)
 * - "www.abc" => "abc"
 * - "abc" => "abc"
 */
function normalizeSubdomainPart(subdomainPart: string): string {
  if (!subdomainPart) return "";
  if (RESERVED_ROOT_SUBDOMAINS.has(subdomainPart)) return "";
  if (subdomainPart.startsWith("www.")) {
    return subdomainPart.slice(4);
  }
  return subdomainPart;
}

/**
 * Returns true when hostname is a public subdomain of the given suffix (e.g. aivy.pitaliy.com).
 * Returns false for bare domain (pitaliy.com) or non-matching host (localhost, other domains).
 */
export function isPublicSubdomainHost(
  hostname: string,
  domainSuffix: string,
): boolean {
  const normalized = normalizeDomainSuffix(domainSuffix);
  const normalizedHostname = hostname.toLowerCase();
  if (!normalized || !normalizedHostname) return false;
  if (normalizedHostname === normalized) return false;
  if (!normalizedHostname.endsWith(`.${normalized}`)) return false;
  const rawSubdomainPart = normalizedHostname.slice(0, -(normalized.length + 1));
  const subdomainPart = normalizeSubdomainPart(rawSubdomainPart);
  if (!subdomainPart) return false;
  if (RESERVED_ROOT_SUBDOMAINS.has(subdomainPart)) return false;
  return true;
}

/**
 * Gets the subdomain from hostname using the known domain suffix (e.g. "aivy" from "aivy.pitaliy.com").
 * Returns empty string if hostname is not a public subdomain.
 */
export function getSubdomainFromHost(
  hostname: string,
  domainSuffix: string,
): string {
  const normalized = normalizeDomainSuffix(domainSuffix);
  const normalizedHostname = hostname.toLowerCase();
  if (!normalized || !normalizedHostname || normalizedHostname === normalized)
    return '';
  if (!normalizedHostname.endsWith(`.${normalized}`)) return '';
  const rawSubdomainPart = normalizedHostname.slice(0, -(normalized.length + 1));
  const subdomainPart = normalizeSubdomainPart(rawSubdomainPart);
  if (!subdomainPart) return '';
  if (RESERVED_ROOT_SUBDOMAINS.has(subdomainPart)) return '';
  return subdomainPart;
}
